<template class="RechargeInvoicePayments">
    <div v-if="isLoading" class="content-container">
        <div class="loading">
            <span class="loader"></span>
        </div>
    </div>
    <div v-else class="content-container">
        <div class="top-content">
            <div class="flex-row-centered">
                <MarketSelector />
                <PeriodSelector v-model=period hasNullSelector id=periodSelector showTitle @update:model-value=updateSelection 
                        :filter="(p)=>allPayments?.some(x=>p.within(x.periodMonth))"/>
                <div class="center-buttons">
                    <button :disabled=!hasCheckboxes
                        @click="proceedToPayment"
                        class="incident-nav-button"
                    >
                        Confirm Payment
                    </button>
                </div>
            </div>
        </div>
        <div class="table-container">
            <TableLite
                :Rows="displayedPayments"
                :Cols="cols"
                :Labels="labels"
                :RowsPerPage="50"
                :HasCheckBox=hasCheckboxes
                :UpdateCheckedRows="updateCheckedRows"
                :checkAll="false"
                :checkedRow=checkedRow
            />
        </div>

        <RechargeInvoicePaymentPopUp
            v-model=selectedRow
            @confirm=refreshAfterConfirm
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, provide } from "vue";
import { marketSelection } from "@/services/helpers/Helper";
import PaymentItem from "@/Interfaces/Payments/RechargeInvoicePaymentItem";
import { rechargeInvoicePayments } from '@/services/network/index'
import DatePeriod from "@/types/DatePeriod";
import TableLite from "@/components/TableLite.vue";
import RechargeInvoicePaymentPopUp from "@/components/RechargeInvoicePaymentPopUp.vue";
import PeriodSelector from "@/components/PeriodSelector.vue";
import MarketSelector from "@/components/MarketSelector.vue";
import useNotifier from "@/services/composables/Notifier";
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";

const cols: Array<keyof PaymentItem> = [
  "invoiceId",
  "balance",
  "amountTotal",
  "amountVAT",
  "issueDate",
  "dueDate",
  "balanceDateUpdated"
];
const labels = [
  "Invoice ID",
  "Balance",
  "Amount Total",
  "Amount VAT",
  "Issue Date",
  "Due Date",
  "Balance Date Updated"
];

const page = ref(1);
provide("page", page);

// Table
const isLoading = ref(false);
const allPayments = ref<PaymentItem[]>([]);
const period = ref(new DatePeriod());

const displayedPayments = computed(() =>
    period.value.isValid() ?
        allPayments.value?.filter(x => x.periodMonth.valueOf() === period.value.valueOf()) :
        allPayments.value
);
const checkedRow = ref<PaymentItem>();
const hasCheckboxes = ref(false);
const selectedRow = ref<PaymentItem>();

onMounted(async () => {
    marketSelection.value = "GB";
    getPayments();
});
async function getPayments() {
    //const errorType = 'Getting Recharge Invoices from API';
    try{
        isLoading.value = true;
        allPayments.value = (await rechargeInvoicePayments.get()).data.map(x=>new PaymentItem(x));

        if (period.value.isValid()) {
            updateSelection();
        }
    }catch(error) {
        useNotifierWithErrFormatter().error({error, errorType: "Getting Recharge Invoices"})
    }finally{
        isLoading.value = false;
    }
}
const updateSelection = () => {
    checkedRow.value = undefined;
    selectedRow.value = undefined;

    hasCheckboxes.value = period.value.isValid();
};
const proceedToPayment = () => {
    selectedRow.value = checkedRow.value;
    if (!selectedRow.value) useNotifier().notify({msg:'No item selected'});
};
interface RowWrapper<T> {
    checked: boolean;
    value: T;
}
function updateCheckedRows(row: RowWrapper<any>, rows: RowWrapper<any>[]) {
    if (row.value == checkedRow.value) {
        row.checked = false;
        checkedRow.value = undefined;
    } else {
        rows.forEach(x=>x.checked = false);
        row.checked = true;
        checkedRow.value = row.value;
    }
}
function refreshAfterConfirm() {
    getPayments();
    checkedRow.value = undefined;
}
</script>